<template>
  <generic-modal
    :title="$t('order confirmation') || 'Confirmación de Pedido'"
    ref="generic_modal"
    class="confirmation-modal"
  >
    <!-- Icono de éxito principal -->
    <div class="success-header">
      <div class="success-icon-container">
        <div class="success-icon">
          <i class="fa fa-check"></i>
        </div>
        <div class="success-ripple"></div>
      </div>
      <h2 class="success-title">
        {{ $t("order confirmed") }}
      </h2>
      <p class="success-subtitle">
        {{ $t("thank you for your purchase") }}
      </p>
    </div>

    <!-- Información del pedido -->
    <div class="order-info-section">
      <div class="order-card">
        <div class="order-header">
          <div class="order-number-section">
            <span class="order-label">{{ $t("order number") }}</span>
            <div class="order-number-display">
              <span class="order-hash">#</span>
              <span class="order-id">{{ order.order_id }}</span>
              <button
                class="copy-order-btn"
                @click="copyOrderNumber"
                :title="$t('copy order number') || 'Copiar número de pedido'"
              >
                <i class="fa fa-copy"></i>
              </button>
            </div>
          </div>
          <div class="order-status">
            <span class="status-badge">
              <i class="fa fa-clock"></i>
              {{ $t("processing") }}
            </span>
          </div>
        </div>

        <div class="order-details" v-if="order.items && order.items.length">
          <h4 class="details-title">
            {{ $t("order summary") }}
          </h4>
          <div class="order-items">
            <div
              class="order-item"
              v-for="item in order.items"
              :key="item.product_id"
            >
              <div class="item-info">
                <span class="item-name">{{ item.name }}</span>
                <span class="item-quantity">x{{ item.quantity }}</span>
              </div>
              <span class="item-price">{{
                formatCurrency(item.price * item.quantity)
              }}</span>
            </div>
          </div>
          <div class="order-total">
            <span class="total-label">{{ $t("total") }}:</span>
            <span class="total-amount">{{ formatCurrency(order.total) }}</span>
          </div>
        </div>

        <div class="payment-info" v-if="order.payment_method">
          <div class="payment-row">
            <span class="payment-label">{{ $t("payment method") }}:</span>
            <div class="payment-method">
              <i class="fa" :class="getPaymentIcon(order.payment_method)"></i>
              <span>{{ getPaymentMethodName(order.payment_method) }}</span>
            </div>
          </div>
          <div
            class="payment-row"
            v-if="order.payment_detail && order.payment_detail.date"
          >
            <span class="payment-label">{{ $t("payment date") }}:</span>
            <span class="payment-date">{{
              formatDate(order.payment_detail.date)
            }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Información de entrega -->
    <div class="delivery-info" v-if="order.address">
      <div class="info-card">
        <div class="info-header">
          <i class="fa fa-truck"></i>
          <h4>{{ $t("delivery information") }}</h4>
        </div>
        <div class="delivery-details">
          <p>
            <strong>{{ order.name }}</strong>
          </p>
          <p>{{ order.address }}</p>
          <p v-if="order.phone">{{ $t("phone") }}: {{ order.phone }}</p>
          <p v-if="order.email">{{ $t("email") }}: {{ order.email }}</p>
        </div>
      </div>
    </div>

    <!-- Próximos pasos -->
    <div class="next-steps">
      <h4 class="steps-title">
        {{ $t("what happens next") }}
      </h4>
      <div class="steps-list">
        <div class="step-item">
          <div class="step-icon">
            <i class="fa fa-envelope"></i>
          </div>
          <div class="step-content">
            <h5>{{ $t("confirmation email") }}</h5>
            <p>
              {{
                $t("email confirmation description") ||
                "Recibirás un email con los detalles de tu pedido"
              }}
            </p>
          </div>
        </div>
        <div class="step-item">
          <div class="step-icon">
            <i class="fa fa-cogs"></i>
          </div>
          <div class="step-content">
            <h5>{{ $t("order processing") }}</h5>
            <p>
              {{
                $t("processing description") ||
                "Preparamos tu pedido para el envío"
              }}
            </p>
          </div>
        </div>
        <div class="step-item">
          <div class="step-icon">
            <i class="fa fa-truck"></i>
          </div>
          <div class="step-content">
            <h5>{{ $t("delivery") }}</h5>
            <p>
              {{
                $t("delivery description") ||
                "Te notificaremos cuando tu pedido esté en camino"
              }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <template v-slot:footer>
      <div class="modal-footer-actions">
        <button class="button is-light is-medium" @click="printOrder">
          <span class="icon">
            <i class="fa fa-print"></i>
          </span>
          <span>{{ $t("print order") }}</span>
        </button>
        <button class="button is-primary is-medium" @click="close_modal">
          <span class="icon">
            <i class="fa fa-shopping-cart"></i>
          </span>
          <span>{{ $t("continue shopping") }}</span>
        </button>
      </div>
    </template>
  </generic-modal>
</template>

<script>
import {
  put,
  error_message,
  get,
  post,
  error_alert,
  generate_error_message_text,
  get_currency_settings,
  format_currency,
} from "@/utils";
import GenericModal from "@/components/generic_modal/Index.vue";

export default {
  name: "OnlineStoreConfirmOrderModal",
  components: {
    GenericModal,
  },
  data() {
    return {
      order: {},
    };
  },
  computed: {},
  methods: {
    close_modal() {
      let v = this;
      v.$refs.generic_modal.close_modal();
      v.set_initial_state();
      v.$emit("closed");
    },
    show(order) {
      let v = this;
      v.set_initial_state();
      v.order = order;
      v.$refs.generic_modal.show_modal();
    },
    set_initial_state() {
      let v = this;
      v.order = {};
    },
    formatCurrency(amount) {
      return format_currency(amount ? amount.toFixed(2) : "0.00");
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    getPaymentIcon(method) {
      const icons = {
        card: "fa-credit-card",
        credit_card: "fa-credit-card",
        deposit: "fa-university",
        bank_deposit: "fa-university",
        cash: "fa-money",
        paypal: "fa-paypal",
      };
      return icons[method] || "fa-credit-card";
    },
    getPaymentMethodName(method) {
      const names = {
        card: this.$t("credit card"),
        credit_card: this.$t("credit card"),
        deposit: this.$t("bank deposit"),
        bank_deposit: this.$t("bank deposit"),
        cash: this.$t("cash"),
        paypal: "PayPal",
      };
      return names[method] || method;
    },
    copyOrderNumber() {
      const orderNumber = `#${this.order.order_id}`;
      navigator.clipboard
        .writeText(orderNumber)
        .then(() => {
          this.$toast.success(this.$t("order number copied"));
        })
        .catch(() => {
          // Fallback para navegadores más antiguos
          const textArea = document.createElement("textarea");
          textArea.value = orderNumber;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
          this.$toast.success(this.$t("order number copied"));
        });
    },
    printOrder() {
      window.print();
    },
  },
  mounted() {},
  watch: {},
};
</script>
<style scoped>
/* Variables CSS para el modal de confirmación */
:root {
  --confirm-success: #4ecdc4;
  --confirm-primary: #667eea;
  --confirm-secondary: #764ba2;
  --confirm-dark: #2c3e50;
  --confirm-light: #f8f9fa;
  --confirm-border: #e9ecef;
  --confirm-gray: #6c757d;
  --confirm-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  --confirm-shadow-light: 0 2px 10px rgba(0, 0, 0, 0.08);
  --confirm-radius: 12px;
  --confirm-transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Modal principal */
.confirmation-modal :deep(.modal-card) {
  max-width: 600px;
  border-radius: var(--confirm-radius);
  overflow: hidden;
}

.confirmation-modal :deep(.modal-card-head) {
  background: linear-gradient(135deg, var(--confirm-success) 0%, #44a08d 100%);
  color: white;
  border: none;
  padding: 2rem 2rem 1rem;
}

.confirmation-modal :deep(.modal-card-title) {
  color: white !important;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
}

.confirmation-modal :deep(.modal-card-body) {
  padding: 0;
}

/* Header de éxito */
.success-header {
  text-align: center;
  padding: 2rem;
  /* background: linear-gradient(180deg, #f8fdfc 0%, #ffffff 100%); */
  border-bottom: 1px solid var(--confirm-border);
  position: relative;
}

.success-icon-container {
  position: relative;
  display: inline-block;
  margin-bottom: 1.5rem;
}

.success-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--confirm-success) 0%, #44a08d 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2rem;
  box-shadow: var(--confirm-shadow);
  position: relative;
  z-index: 2;
  animation: successPulse 2s ease-out;
}

.success-ripple {
  position: absolute;
  top: 0;
  left: 0;
  width: 80px;
  height: 80px;
  border: 3px solid var(--confirm-success);
  border-radius: 50%;
  opacity: 0;
  animation: rippleEffect 2s ease-out infinite;
}

.success-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--confirm-dark);
  margin-bottom: 0.5rem;
}

.success-subtitle {
  font-size: 1.1rem;
  color: var(--confirm-gray);
  margin: 0;
}

/* Información del pedido */
.order-info-section {
  padding: 2rem;
}

.order-card {
  background: white;
  border: 1px solid var(--confirm-border);
  border-radius: var(--confirm-radius);
  overflow: hidden;
  box-shadow: var(--confirm-shadow-light);
}

.order-header {
  background: #464646 !important;
  /* background: linear-gradient(135deg, var(--confirm-light) 0%, #ffffff 100%); */
  padding: 1.5rem;
  border-bottom: 1px solid var(--confirm-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.order-number-section {
  flex: 1;
}

.order-label {
  display: block;
  font-size: 0.9rem;
  color: var(--confirm-gray);
  margin-bottom: 0.5rem;
}

.order-number-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.order-hash {
  font-size: 1.2rem;
  color: var(--confirm-gray);
  font-weight: 600;
}

.order-id {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--confirm-primary);
  font-family: "Courier New", monospace;
}

.copy-order-btn {
  background: var(--confirm-primary);
  color: white;
  border: none;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--confirm-transition);
}

.copy-order-btn:hover {
  background: #5a6fd8;
  transform: scale(1.05);
}

.order-status {
  margin-left: 1rem;
}

.status-badge {
  background: #fff3cd;
  color: #856404;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Detalles del pedido */
.order-details {
  padding: 1.5rem;
}

.details-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--confirm-dark);
  margin-bottom: 1rem;
}

.order-items {
  margin-bottom: 1rem;
}

.order-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.order-item:last-child {
  border-bottom: none;
}

.item-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.item-name {
  font-weight: 500;
  color: var(--confirm-dark);
}

.item-quantity {
  background: var(--confirm-light);
  color: var(--confirm-gray);
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.item-price {
  font-weight: 600;
  color: var(--confirm-primary);
}

.order-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0 0;
  border-top: 2px solid var(--confirm-border);
  margin-top: 1rem;
}

.total-label {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--confirm-dark);
}

.total-amount {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--confirm-success);
}

/* Información de pago */
.payment-info {
  padding: 1.5rem;
  background: #f8f9fa;
  border-top: 1px solid var(--confirm-border);
}

.payment-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.payment-row:last-child {
  margin-bottom: 0;
}

.payment-label {
  font-weight: 500;
  color: var(--confirm-gray);
}

.payment-method {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--confirm-dark);
}

.payment-date {
  font-weight: 600;
  color: var(--confirm-dark);
}

/* Información de entrega */
.delivery-info {
  padding: 0 2rem 2rem;
}

.info-card {
  background: white;
  border: 1px solid var(--confirm-border);
  border-radius: var(--confirm-radius);
  padding: 1.5rem;
  box-shadow: var(--confirm-shadow-light);
}

.info-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  color: var(--confirm-primary);
}

.info-header i {
  font-size: 1.2rem;
}

.info-header h4 {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: var(--confirm-dark);
}

.delivery-details p {
  margin-bottom: 0.5rem;
  color: var(--confirm-dark);
}

.delivery-details p:last-child {
  margin-bottom: 0;
}

/* Próximos pasos */
.next-steps {
  padding: 2rem;
  /* background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%); */
  border-top: 1px solid var(--confirm-border);
}

.steps-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--confirm-dark);
  margin-bottom: 1.5rem;
  text-align: center;
}

.steps-list {
  display: grid;
  gap: 1.5rem;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.step-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(
    135deg,
    var(--confirm-primary) 0%,
    var(--confirm-secondary) 100%
  );
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1rem;
}

.step-content h5 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--confirm-dark);
  margin-bottom: 0.25rem;
}

.step-content p {
  font-size: 0.9rem;
  color: var(--confirm-gray);
  line-height: 1.4;
  margin: 0;
}

/* Footer del modal */
.modal-footer-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  padding: 1.5rem 2rem;
  background: var(--confirm-light);
  border-top: 1px solid var(--confirm-border);
}

.modal-footer-actions .button {
  border-radius: var(--confirm-radius);
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  transition: var(--confirm-transition);
}

.modal-footer-actions .button.is-primary {
  background: linear-gradient(
    135deg,
    var(--confirm-primary) 0%,
    var(--confirm-secondary) 100%
  );
  border: none;
}

.modal-footer-actions .button:hover {
  transform: translateY(-2px);
  box-shadow: var(--confirm-shadow-light);
}

/* Animaciones */
@keyframes successPulse {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes rippleEffect {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .confirmation-modal :deep(.modal-card) {
    margin: 1rem;
    max-width: calc(100vw - 2rem);
  }

  .confirmation-modal :deep(.modal-card-head) {
    padding: 1.5rem;
  }

  .success-header {
    padding: 1.5rem;
  }

  .order-info-section {
    padding: 1.5rem;
  }

  .order-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .order-status {
    margin-left: 0;
    text-align: center;
  }

  .next-steps {
    padding: 1.5rem;
  }

  .modal-footer-actions {
    flex-direction: column;
    padding: 1.5rem;
  }
}

@media (max-width: 480px) {
  .success-icon {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
  }

  .success-ripple {
    width: 60px;
    height: 60px;
  }

  .success-title {
    font-size: 1.5rem;
  }

  .order-item {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .item-info {
    justify-content: space-between;
  }
}

/* Estilos para impresión */
@media print {
  .confirmation-modal :deep(.modal-card-head),
  .modal-footer-actions {
    display: none !important;
  }

  .confirmation-modal :deep(.modal-card) {
    box-shadow: none !important;
    border: 1px solid #000 !important;
  }

  .success-header {
    border-bottom: 2px solid #000 !important;
  }

  .order-card {
    border: 1px solid #000 !important;
  }
}
</style>
